// schema.org JSON-LD for every page, shared by <Seo> and the Worker so the markup
// in the first HTML response matches what the app renders.
import { ARTIST, HOME, PAGES, SITE_URL, absoluteUrl, apiAssetUrl, artworkPath } from "./site";
import { CREDENTIALS, FAQS } from "../content/chapters";

const PERSON_ID = `${SITE_URL}/#artist`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const LANG = "en-IN";

const person = () => ({
  "@type": "Person",
  "@id": PERSON_ID,
  name: ARTIST.name,
  alternateName: ARTIST.alternateNames,
  honorificPrefix: "Dr",
  jobTitle: ARTIST.title,
  description: PAGES.artist.description,
  url: absoluteUrl(PAGES.artist.path),
  image: PAGES.artist.image,
  telephone: ARTIST.phone,
  email: ARTIST.email,
  sameAs: [ARTIST.instagram, ARTIST.facebook],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Bhopal",
    addressRegion: "Madhya Pradesh",
    addressCountry: "IN",
  },
  knowsAbout: ["Mithila painting", "Madhubani painting", "Indian folk art", "Art education"],
  hasCredential: CREDENTIALS.filter((c) => c.label === "Government of India").map((c) => ({
    "@type": "EducationalOccupationalCredential",
    name: c.title,
    description: c.text,
    credentialCategory: "license",
  })),
});

const website = () => ({
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: `${SITE_URL}/`,
  name: ARTIST.name,
  alternateName: [`${ARTIST.name} — Mithila Art`, ...ARTIST.alternateNames],
  description: HOME.description,
  inLanguage: LANG,
  publisher: { "@id": PERSON_ID },
  about: { "@id": PERSON_ID },
});

// trail: [{ name, path }] from Home to the current page.
const breadcrumb = (url, trail) => ({
  "@type": "BreadcrumbList",
  "@id": `${url}#breadcrumb`,
  itemListElement: trail.map((crumb, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: crumb.name,
    item: absoluteUrl(crumb.path),
  })),
});

const webPage = ({ url, type = "WebPage", name, description, image, trail }) => ({
  "@type": type,
  "@id": `${url}#webpage`,
  url,
  name,
  description,
  inLanguage: LANG,
  isPartOf: { "@id": WEBSITE_ID },
  about: { "@id": PERSON_ID },
  ...(image && { primaryImageOfPage: { "@type": "ImageObject", url: absoluteUrl(image) } }),
  ...(trail && { breadcrumb: { "@id": `${url}#breadcrumb` } }),
});

const artwork = (art) => {
  const url = absoluteUrl(artworkPath(art));
  const image = apiAssetUrl(art.image);
  return {
    "@type": "VisualArtwork",
    "@id": `${url}#artwork`,
    url,
    name: art.title,
    ...(art.title_hi && { alternateName: art.title_hi }),
    image: {
      "@type": "ImageObject",
      url: image,
      contentUrl: image,
      ...(art.width && { width: art.width, height: art.height }),
      caption: art.title,
      creator: { "@id": PERSON_ID },
      creditText: ARTIST.name,
      copyrightNotice: `© ${ARTIST.name}`,
    },
    description: art.intro || (art.story || [])[0] || undefined,
    artform: "Painting",
    genre: "Mithila (Madhubani) painting",
    artMedium: art.medium || undefined,
    dateCreated: art.year || undefined,
    keywords: [...(art.themes || []), ...(art.motifs || [])].join(", ") || undefined,
    creator: { "@id": PERSON_ID },
    copyrightHolder: { "@id": PERSON_ID },
    inLanguage: LANG,
  };
};

const graph = (nodes) => ({ "@context": "https://schema.org", "@graph": nodes });

export const homeSchema = () =>
  graph([website(), person(), webPage({ url: `${SITE_URL}/`, name: HOME.title, description: HOME.description, image: HOME.image })]);

const SECTION_TYPES = {
  artist: "ProfilePage",
  gallery: "CollectionPage",
  world: "FAQPage",
  contact: "ContactPage",
};

// key: a PAGES key. artworks: only used on the gallery page.
export const sectionSchema = (key, { artworks = [] } = {}) => {
  const page = PAGES[key];
  const url = absoluteUrl(page.path);
  const trail = [{ name: HOME.name, path: "/" }, { name: page.name, path: page.path }];
  const node = webPage({ url, type: SECTION_TYPES[key], name: page.title, description: page.description, trail });
  if (key === "artist") node.mainEntity = { "@id": PERSON_ID };
  if (key === "world") {
    node.mainEntity = FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    }));
  }
  if (key === "gallery" && artworks.length) {
    node.mainEntity = {
      "@type": "ItemList",
      numberOfItems: artworks.length,
      itemListElement: artworks.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: absoluteUrl(artworkPath(a)),
        name: a.title,
        image: apiAssetUrl(a.image),
      })),
    };
  }
  return graph([website(), person(), node, breadcrumb(url, trail)]);
};

export const artworkSchema = (art) => {
  const url = absoluteUrl(artworkPath(art));
  const trail = [
    { name: HOME.name, path: "/" },
    { name: PAGES.gallery.name, path: PAGES.gallery.path },
    { name: art.title, path: artworkPath(art) },
  ];
  const page = webPage({ url, type: "ItemPage", name: art.title, description: art.intro, trail });
  page.mainEntity = { "@id": `${url}#artwork` };
  page.primaryImageOfPage = { "@type": "ImageObject", url: apiAssetUrl(art.image) };
  return graph([website(), person(), page, artwork(art), breadcrumb(url, trail)]);
};
